import React, { useState, useEffect } from "react";
import * as Dialog from '@radix-ui/react-dialog';
import { CalendarDays, Download, X } from "lucide-react";
import { inquiryService } from "../../services/inquiryService";

export default function ShettigreInquiryDialog({
  isScheduleVisitDialogOpen,
  setIsScheduleVisitDialogOpen,
  isBrochureDialogOpen,
  setIsBrochureDialogOpen,
  formData,
  handleChange,
}) {
  const [submitStatus, setSubmitStatus] = useState(null); 
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsScheduleVisitDialogOpen(true);
    }, 3000); 

    return () => clearTimeout(timer);
  }, [setIsScheduleVisitDialogOpen]); 

  const handleSubmit = async (e, inquiryType) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const property_url = window.location.href;
      const property_id = "GodrejShettigere"; 

      let finalMessage = formData.message;
      if (inquiryType === 'brochure') {
        finalMessage = finalMessage ? `Brochure Request: ${finalMessage}` : "Brochure Request";
      } else if (inquiryType === 'scheduleVisit') {
        finalMessage = finalMessage ? `Schedule Visit Request: ${finalMessage}` : "Schedule Visit Request";
      }

      await inquiryService.createPropertyInquiry({
        ...formData,
        message: finalMessage,
        property_url,
        property_id,
        status: 'new'
      });
      setSubmitStatus('success');
      setTimeout(() => {
        if (inquiryType === 'scheduleVisit') {
          setIsScheduleVisitDialogOpen(false);
        } else {
          setIsBrochureDialogOpen(false);
        }
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error submitting property inquiry:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderForm = (inquiryType) => (
    <form onSubmit={(e) => handleSubmit(e, inquiryType)} className="space-y-4">
      {submitStatus === 'success' && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Success!</strong>
          <span className="block sm:inline"> Your inquiry has been submitted.</span>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> Failed to submit your inquiry. Please try again.</span>
        </div>
      )}
      <input
        type="text"
        name="name"
        placeholder="Full Name"
        value={formData.name}
        onChange={handleChange}
        required
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-200"
      />
      <input
        type="tel"
        name="phone"
        placeholder="Mobile No"
        value={formData.phone}
        onChange={handleChange}
        required
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-200"
      />
      <input
        type="email"
        name="email"
        placeholder="E-Mail Address"
        value={formData.email}
        onChange={handleChange}
        required
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-200"
      />
      <select
        name="interestedIn"
        value={formData.interestedIn}
        onChange={handleChange}
        required
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent bg-white text-gray-700 appearance-none transition-colors duration-200"
      >
        <option value="">Interested In</option>
        <option value="2bhk">2 BHK</option>
        <option value="3bhk_2t">3 BHK (2 Toilets)</option>
        <option value="3bhk_3t">3 BHK (3 Toilets)</option>
        <option value="other">Other</option>
      </select>
      <textarea
        name="message"
        placeholder="Your Message (Optional)"
        rows="3"
        value={formData.message}
        onChange={handleChange}
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-200"
      ></textarea>
      <button type="submit" disabled={isSubmitting} className="w-full bg-accent text-white font-bold py-2 rounded-md hover:bg-primary-700 transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl">
        {isSubmitting ? 'Submitting...' : inquiryType === 'scheduleVisit' ? <><CalendarDays className="w-5 h-5 mr-2" /> Schedule A Visit</> : <><Download className="w-5 h-5 mr-2" /> Download Brochure</>}
      </button>
    </form>
  );

  return (
    <>
      {/* Schedule A Visit Dialog */}
      <Dialog.Root open={isScheduleVisitDialogOpen} onOpenChange={setIsScheduleVisitDialogOpen}>
        <Dialog.Trigger asChild>
          <button className="fixed right-0 top-1/2 -translate-y-1/2 z-50 bg-accent text-white py-3 px-2 mr-4 rounded-l-lg font-semibold text-sm transform -rotate-90 origin-right hover:bg-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center">
            <CalendarDays className="w-5 h-5 mr-2 rotate-90" />
            SCHEDULE A VISIT
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm data-[state=open]:animate-overlayShow z-50" />
          <Dialog.Content className="fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-4xl translate-x-[-50%] translate-y-[-50%] rounded-xl bg-white p-0 shadow-2xl focus:outline-none data-[state=open]:animate-contentShow z-[51]">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="hidden md:block md:w-1/2">
                <img
                  src="https://new-launch-shettigere.in/images/hero.jpg"
                  alt="Godrej Shettigere Inquiry"
                  className="rounded-lg object-cover w-full h-full shadow-lg"
                />
              </div>
              <div className="w-full md:w-1/2 flex flex-col justify-center p-1 md:p-3">
                <Dialog.Title className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-2 text-center leading-tight">
                  Schedule A Visit
                </Dialog.Title>
                <Dialog.Description className="text-gray-600 text-sm text-center mb-4">
                  Fill out the form below to get more details and special pricing for Godrej Shettigere.
                </Dialog.Description>
                {renderForm('scheduleVisit')}
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                className="absolute top-0 right-0 text-gray-400 hover:text-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1"
                aria-label="Close"
              >
                <X />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Brochure Dialog */}
      <Dialog.Root open={isBrochureDialogOpen} onOpenChange={setIsBrochureDialogOpen}>
        <Dialog.Trigger asChild>
          <button className="fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-accent text-white py-3 px-2 ml-4 rounded-r-lg font-semibold text-sm transform rotate-90 origin-left hover:bg-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center">
            <Download className="w-5 h-5 mr-2 -rotate-90" />
            DOWNLOAD BROCHURE
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm data-[state=open]:animate-overlayShow z-50" />
          <Dialog.Content className="fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-4xl translate-x-[-50%] translate-y-[-50%] rounded-xl bg-white p-0 shadow-2xl focus:outline-none data-[state=open]:animate-contentShow z-[51]">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="hidden md:block md:w-1/2">
                <img
                  src="https://new-launch-shettigere.in/images/hero.jpg"
                  alt="Godrej Shettigere Inquiry"
                  className="rounded-lg object-cover w-full h-full shadow-lg"
                />
              </div>
              <div className="w-full md:w-1/2 flex flex-col justify-center p-3">
                <Dialog.Title className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2 text-center leading-tight">
                  Download Brochure
                </Dialog.Title>
                <Dialog.Description className="text-gray-600 text-sm text-center mb-4">
                  Fill out the form below to download the brochure for Godrej Shettigere.
                </Dialog.Description>
                {renderForm('brochure')}
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                className="absolute top-0 right-0 text-gray-400 hover:text-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1"
                aria-label="Close"
              >
                <X />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}